import {
  getPositionSourceName,
  getPositionSourceNameForTrackCompany
} from 'react/utils/coordinateUtils';
import { getResourceTo } from 'react/utils/utils';

const seriesConfig = {
  BOOLEAN: {
    type: 'step',
    parseValue: (d) => (d.value ? 1 : 0)
  },
  INTEGER: {
    type: 'line',
    parseValue: (d) => parseInt(d.value, 10)
  },
  DECIMAL: {
    type: 'spline',
    parseValue: (d) => parseFloat(d.value),
    extra: { tooltip: { decimal: 2 } }
  }
};

export const MAP_TYPE_RANGE = {
  minute: 0,
  hour: 1,
  day: 2,
  month: 3,
  year: 4,
  all: 5
};

function buildSeries({ capabilityKey, telematicsList, capabilityType, color }) {
  const config = seriesConfig[capabilityType];
  if (!config) return { series: { data: [] } };
  const { type, parseValue } = config;
  const data = telematicsList[capabilityKey].map((item, index) => ({
    x: item.date,
    y: parseValue(item),
    position: item.position,
    color
  }));
  return {
    data,
    lastDateOnSerie: data[data.length - 1]?.x,
    type,
    ...config.extra
  };
}

export const groupChartSeriesDataByCapability = (data) =>
  data.reduce(
    (acc, { key, source, sourceType, color, telematicsList, capabilities }) => {
      capabilities.forEach(
        ({ key: capabilityKey, type: capabilityType, id: capabilityId }) => {
          if (!telematicsList[capabilityKey]) return;
          if (!acc[capabilityKey]) acc[capabilityKey] = [];
          const telematic = { key, source, sourceType };
          const name =
            sourceType === 'MOBILE'
              ? getPositionSourceName(telematic)
              : getPositionSourceNameForTrackCompany(telematic);
          acc[capabilityKey].push({
            custom: { capability: capabilityKey, capabilityId, capabilityType },
            series: {
              name,
              yAxis: capabilityKey,
              color,
              ...buildSeries({
                capabilityKey,
                telematicsList,
                capabilityType,
                color
              })
            }
          });
        }
      );
      return acc;
    },
    {}
  );

export const getYAxisTitleByCapability = (
  capability,
  capabilitiesFromTrackCompany
) => {
  const { unitSystem } = Monet.Session.User;
  const unitSystemDDescription =
    unitSystem === 'NON_METRIC'
      ? 'Greenmile.UnitMetrics.milesPerHour'
      : 'Greenmile.UnitMetrics.kilometersPerHour';
  const capabilityMap = {
    speed: `${getResourceTo('Greenmile.Telematic.speedDescription')} (${getResourceTo(unitSystemDDescription)})`
  };
  return (
    capabilityMap[capability] ||
    capabilitiesFromTrackCompany.find((c) => c.key === capability)
      ?.description ||
    capability
  );
};

export const getChartData = (
  sourcesWithCapabilities,
  capabilitiesFromTrackCompany,
  sessionData,
  zoomButtonsClick
) => {
  const groupedData = groupChartSeriesDataByCapability(sourcesWithCapabilities);
  return Object.keys(groupedData).map((key) => {
    const { custom } = groupedData[key][0];
    return {
      title: {
        text: getYAxisTitleByCapability(key, capabilitiesFromTrackCompany),
        align: 'left',
        style: { fontWeight: 'bold', fontSize: '14px', color: '#1A2127' }
      },
      yAxis: {
        id: key,
        lineWidth: 2,
        height: '95%',
        custom: { ...custom, sessionData }
      },
      xAxis: {
        events: {
          afterSetExtremes: function (e) {
            if (typeof zoomButtonsClick === 'function') {
              zoomButtonsClick(e);
            }
          }
        }
      },
      series: groupedData[key].map((data) => ({
        ...data.series,
        events: {
          click: function ({ point }) {
            const {
              position,
              series: {
                yAxis: {
                  options: {
                    custom: { sessionData }
                  }
                }
              }
            } = point;
            if (position) {
              Greenmile.Map.Utility.setMarkerFocus(sessionData, position);
            }
          }
        }
      }))
    };
  });
};
